import {
  type Adjacents,
  type FocusedItem,
  type SelectionIntent,
  type SelectionsReader
} from '@databases/selections-api-types';

import { type DefaultRootState } from '../redux.ts';

import {
  selectCurrentSelectionMode,
  selectFocusedItem,
  selectHasEntryAfterSelection,
  selectHasEntryBeforeSelection,
  selectHasFieldAfterSelection,
  selectHasFieldBeforeSelection,
  selectHasSelectedCells,
  selectIsDeleteColumnsDisabled,
  selectIsEntireEntrySelected,
  selectIsHoverDeleteEntriesButton,
  selectIsHoverDeleteFieldsButton,
  selectIsEntireFieldSelected,
  selectIsSoftSelectEnabled,
  selectIsValueSelected,
  selectSelectedAdjacentCells,
  selectSelectedEntriesCount,
  selectSelectedEntireFieldsCount,
  selectSelectedEntryIds,
  selectSelectedFieldIds,
  selectSelectedValuesCount,
  selectSelectedEntireEntryIds,
  selectSelectedEntireFieldIds,
  selectSelectedValues
} from './selectors.ts';

interface DatabaseSnapshot {
  entries: readonly string[];
  fields: readonly string[];
}

export interface LegacyReduxSelectionsReaderOptions {
  getState: () => DefaultRootState;
  getContentId: () => string;
  getDatabaseSnapshot: () => DatabaseSnapshot;
}

export class LegacyReduxSelectionsReader implements SelectionsReader {
  #getState: () => DefaultRootState;
  #getContentId: () => string;

  constructor(options: LegacyReduxSelectionsReaderOptions) {
    this.#getState = options.getState;
    this.#getContentId = options.getContentId;
  }

  hasSelection(): boolean {
    const state = this.#getState();
    const contentId = this.#getContentId();

    return selectHasSelectedCells(state, { contentId });
  }

  getSelectionMode() {
    const state = this.#getState();
    const contentId = this.#getContentId();

    if (!selectHasSelectedCells(state, { contentId })) {
      return 'idle' as const;
    }

    return selectCurrentSelectionMode(state, { contentId });
  }

  getSelectedValues(): { entryId: string; fieldId: string }[] {
    return selectSelectedValues(this.#getState(), { contentId: this.#getContentId() });
  }

  getEntryIdsFromSelection(): string[] {
    return selectSelectedEntryIds(this.#getState(), { contentId: this.#getContentId() });
  }

  getSelectedEntryIds(): string[] {
    return selectSelectedEntireEntryIds(this.#getState(), {
      contentId: this.#getContentId()
    });
  }

  getSelectedFieldIds(): string[] {
    return selectSelectedEntireFieldIds(this.#getState(), {
      contentId: this.#getContentId()
    });
  }

  getFieldIdsFromSelection(): string[] {
    return selectSelectedFieldIds(this.#getState(), { contentId: this.#getContentId() });
  }

  getSelectionCount(): number {
    const state = this.#getState();
    const contentId = this.#getContentId();
    const mode = this.getSelectionMode();

    switch (mode) {
      case 'cell':
        return selectSelectedValuesCount(state, { contentId });
      case 'column':
        return selectSelectedEntireFieldsCount(state, { contentId });
      case 'row':
        return selectSelectedEntriesCount(state, { contentId });
      default:
        return 0;
    }
  }

  isEntrySelected(entryId: string): boolean {
    return selectIsEntireEntrySelected(this.#getState(), { entryId });
  }

  isEntryInSelection(entryId: string): boolean {
    return this.getEntryIdsFromSelection().includes(entryId);
  }

  isFieldSelected(fieldId: string): boolean {
    return selectIsEntireFieldSelected(this.#getState(), { fieldId });
  }

  isFieldInSelection(fieldId: string): boolean {
    return this.getFieldIdsFromSelection().includes(fieldId);
  }

  isValueSelected(entryId: string, fieldId: string): boolean {
    const state = this.#getState();

    return selectIsValueSelected(state, { entryId, fieldId });
  }

  getSelectedAdjacentCells(fieldId: string, entryId?: string): Adjacents {
    return selectSelectedAdjacentCells(this.#getState(), {
      contentId: this.#getContentId(),
      fieldId,
      entryId
    });
  }

  hasFieldBeforeSelection(): boolean {
    return selectHasFieldBeforeSelection(this.#getState(), {
      contentId: this.#getContentId()
    });
  }

  hasFieldAfterSelection(): boolean {
    return selectHasFieldAfterSelection(this.#getState(), {
      contentId: this.#getContentId()
    });
  }

  hasEntryBeforeSelection(): boolean {
    return selectHasEntryBeforeSelection(this.#getState(), {
      contentId: this.#getContentId()
    });
  }

  hasEntryAfterSelection(): boolean {
    return selectHasEntryAfterSelection(this.#getState(), {
      contentId: this.#getContentId()
    });
  }

  isDeleteColumnsDisabled(): boolean {
    return selectIsDeleteColumnsDisabled(this.#getState(), {
      contentId: this.#getContentId()
    });
  }

  getSelectionIntent(): SelectionIntent | null {
    const state = this.#getState();

    if (selectIsHoverDeleteEntriesButton(state)) {
      return { type: 'delete', target: 'entries' };
    }

    if (selectIsHoverDeleteFieldsButton(state)) {
      return { type: 'delete', target: 'fields' };
    }

    if (selectIsSoftSelectEnabled(state)) {
      return { type: 'softSelection' };
    }

    return null;
  }

  getFocusedItem(): FocusedItem | undefined {
    return selectFocusedItem(this.#getState(), {
      contentId: this.#getContentId()
    });
  }
}
